import React, { useContext, useState } from 'react';
import { SprawneMiastoContext } from '../../../../components/SprawneMiastoContext';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import api from '../../../../utils/api';
import { TextField, ZXCVBNHelper } from '../../../../components/Form';
import zxcvbnEnPackage from '@zxcvbn-ts/language-pl';
import zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import ExpandBox from '../../../../components/ExpandBox';

export default function Password() {
    const Context = useContext(SprawneMiastoContext);
    const { t } = useTranslation();
    const [changePassword, setChangePassword] = useState<boolean>(false);
    const options = {
        translations: zxcvbnEnPackage.translations,
        graphs: zxcvbnCommonPackage.adjacencyGraphs,
        dictionary: {
            ...zxcvbnCommonPackage.dictionary,
            ...zxcvbnEnPackage.dictionary,
        },
    };
    zxcvbnOptions.setOptions(options);

    return (
        <div>
            <ExpandBox
                show={changePassword}
                header={
                    <div className={'flex flex-col py-4 md:flex-row md:gap-3'}>
                        <div className={'w-full text-slate-600 md:w-1/3'}>
                            {t('translation:MY_ACCOUNT.PROFILE.PASSWORD')}
                        </div>

                        <div className={'flex w-full'}>
                            <input
                                disabled={true}
                                className={
                                    'w-full  rounded-none rounded-l border border-r-0 border-gray-300 px-2 py-1 outline-0 focus:border-blue-200 disabled:text-gray-500'
                                }
                                type={'text'}
                                value={'••••••••••••'}
                            />
                            <button
                                onClick={() => setChangePassword(!changePassword)}
                                className={
                                    'rounded-r border border-SM-yellow px-2 text-SM-yellow transition-all hover:bg-SM-yellow hover:text-white'
                                }>
                                {t('translation:MY_ACCOUNT.PROFILE.CHANGE')}
                            </button>
                        </div>
                    </div>
                }>
                <Formik
                    initialValues={{
                        oldPassword: '',
                        newPassword: '',
                        repeatNewPassword: '',
                    }}
                    validationSchema={Yup.object().shape({
                        oldPassword: Yup.string().required(
                            t('translation:ERRORS.WEB_VALIDATION.0') as string,
                        ),
                        newPassword: Yup.string()
                            .required(t('translation:ERRORS.WEB_VALIDATION.0') as string)
                            .test({
                                name: 'password',
                                message: t('translation:ERRORS.WEB_VALIDATION.1') as string,
                                test: (value) => (value ? zxcvbn(value).score > 2 : false),
                            }),
                        repeatPassword: Yup.string()
                            .required(t('translation:ERRORS.WEB_VALIDATION.0') as string)
                            .oneOf(
                                [Yup.ref('newPassword')],
                                t('translation:ERRORS.WEB_VALIDATION.3') as string,
                            ),
                    })}
                    onSubmit={async (values, formikHelpers) => {
                        const response = await api.patch('/users/me/password', {
                            body: JSON.stringify({
                                oldPassword: values.oldPassword,
                                newPassword: values.newPassword,
                            }),
                        });
                        if (!response.ok) {
                            return formikHelpers.setFieldError(
                                'oldPassword',
                                t('translation:ERRORS.API.2003') as string,
                            );
                        }
                        await Context?.fetchUser();
                        setChangePassword(false);
                        formikHelpers.resetForm();
                    }}>
                    {(fProps) => (
                        <Form className={'mb-5 flex gap-3'}>
                            <div className={'w-1/3'} />
                            <div className={'w-full'}>
                                <TextField
                                    placeholder={
                                        t(
                                            'translation:MY_ACCOUNT.PROFILE.PROVIDE_OLD_PASSWORD',
                                        ) as string
                                    }
                                    type="password"
                                    formik={fProps}
                                    name={'oldPassword'}
                                />
                                <TextField
                                    className={'mt-4'}
                                    placeholder={
                                        t(
                                            'translation:MY_ACCOUNT.PROFILE.REPEAT_NEW_PASSWORD',
                                        ) as string
                                    }
                                    type="password"
                                    formik={fProps}
                                    name={'newPassword'}
                                />

                                {fProps.values.newPassword && (
                                    <ZXCVBNHelper password={fProps.values.newPassword} />
                                )}

                                <TextField
                                    className={'mt-2'}
                                    placeholder={
                                        t(
                                            'translation:MY_ACCOUNT.PROFILE.PROVIDE_PASSWORD',
                                        ) as string
                                    }
                                    type="password"
                                    formik={fProps}
                                    name={'repeatPassword'}
                                />

                                <button
                                    type={'submit'}
                                    className={
                                        'btn-filled mt-2 bg-SM-yellow text-white ring-SM-yellow'
                                    }>
                                    {t('translation:MY_ACCOUNT.PROFILE.SUBMIT')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </ExpandBox>
        </div>
    );
}
